/**
 * `hasChanges` on a supplier order covers two situations that need opposite copy.
 *
 * `supplierOrderChange` sets it while PartsTrader is holding a supplier proposal
 * — new price, part number, or a pushed-out delivery date — and is waiting on us
 * to accept or reject. `supplierConfirmation` also sets it, at the moment the
 * supplier confirms, when the confirmed delivery date differs from the one we
 * ordered against. That second case is a done deal: PartsTrader has committed to
 * the date and exposes nothing to act on, so the only recourse is cancelling.
 *
 * The two are indistinguishable from the fields alone — both land on
 * `newDelivery`. What separates them is whether PartsTrader itself has confirmed
 * the order, since `supplierConfirmation` is what moves it to confirmed.
 */

/** Normalized across the order page and the drawer, which name their ETA fields differently. */
export interface SupplierChangePart {
    id: string;
    buyPrice: number;
    correctedBuyPrice?: number;
    listPrice: number;
    correctedListPrice?: number;
    partNumber?: string;
    correctedPartNumber?: string;
    eta?: string;
    correctedEta?: string;
}

/**
 * Gross margin is deliberately not checked: it is derived from buy and list, so
 * it cannot move on its own.
 */
const hasPricingChange = (part: SupplierChangePart): boolean => (
    (part.correctedBuyPrice != null && part.correctedBuyPrice !== part.buyPrice) ||
    (part.correctedListPrice != null && part.correctedListPrice !== part.listPrice) ||
    (!!part.correctedPartNumber && part.correctedPartNumber !== part.partNumber)
);

const hasEtaChange = (part: SupplierChangePart): boolean => (
    !!part.correctedEta && part.correctedEta !== part.eta
);

export interface SupplierChangeNotice {
    tone: 'warning' | 'info';
    title: string;
    body: string;
}

export interface SupplierChangeSummary {
    notice: SupplierChangeNotice | null;
    /** Parts the Accept/Reject buttons apply to. Empty means no buttons. */
    actionablePartIds: Array<string>;
}

const NOTHING: SupplierChangeSummary = { notice: null, actionablePartIds: [] };

const GENERIC: SupplierChangeSummary = {
    notice: {
        tone: 'warning',
        title: 'Supplier changes',
        body: 'The supplier made changes to this order. You may need to accept them.',
    },
    actionablePartIds: [],
};

export function getSupplierChangeSummary({
    hasChanges,
    source,
    partstraderConfirmed,
    parts,
}: {
    hasChanges?: boolean;
    source?: string;
    /** PartsTrader has confirmed the order itself, not just a local Mark Confirmed. */
    partstraderConfirmed: boolean;
    parts: Array<SupplierChangePart>;
}): SupplierChangeSummary {
    if (!hasChanges) return NOTHING;

    // OpsTrax, CCC and inventory POs raise the flag through their own paths and
    // have no PartsTrader accept/reject semantics to describe.
    if (source !== 'partstrader') return GENERIC;

    const pricing = parts.filter(hasPricingChange);
    if (pricing.length > 0) {
        return {
            notice: {
                tone: 'warning',
                title: 'Supplier changes need review',
                body: 'The supplier proposed new pricing or part numbers. The updated values are shown below — accept them, or reject to cancel the affected parts.',
            },
            actionablePartIds: pricing.map(p => p.id),
        };
    }

    const movedEta = parts.filter(hasEtaChange);
    if (movedEta.length === 0) {
        // Flag left over from a change that has since been applied, or whose
        // parts were cancelled. Nothing to tell the user.
        return NOTHING;
    }

    // The copy stays on what changed rather than what the shop can do about it.
    // Whether the date is still ours to accept depends on which writer set it,
    // and the stored fields do not record that.
    const body = 'The supplier changed the ETA on this order. The updated dates are shown below.';

    if (!partstraderConfirmed) {
        return {
            notice: { tone: 'warning', title: 'Delivery date change proposed', body },
            actionablePartIds: movedEta.map(p => p.id),
        };
    }

    return {
        notice: { tone: 'info', title: 'Delivery dates updated', body },
        actionablePartIds: [],
    };
}
